import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Typography, Button, Modal, Box } from '@mui/material';
import 'bootstrap-icons/font/bootstrap-icons.css';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';

const emptyForm = {
  id: '',
  dst_name: '',
  status: '1',
};

const DstMaster = () => {
  const [dsts, setDsts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // ---- excel import state ----
  const [showImportModal, setShowImportModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  const userInfo = useSelector((state) => state.userInfo);
  const userId = userInfo?.id;

  // ---- get DST master list ----
  const getDsts = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(`${config.API_BASE_URL}get_dst_master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData({
          length: 100,
          start: 0,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setDsts(Array.isArray(result.data?.data) ? result.data.data : []);
        setError('');
      } else {
        setDsts([]);
        setError(result.message || 'No records found.');
      }
    } catch (e) {
      setDsts([]);
      setError('Something went wrong while fetching DST list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getDsts();
  }, [getDsts]);

  const openAddModal = () => {
    setForm(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setForm({
      id: row.id,
      dst_name: row.dst_name || '',
      status: String(row.status ?? '1'),
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---- add / update DST ----
  const handleSave = async () => {
    if (!form.dst_name || !form.dst_name.trim()) {
      setFormError('DST Name is required.');
      return;
    }

    try {
      setSaving(true);
      setFormError('');

      const payload = {
        ...form,
        user_id: userId,
      };

      const response = await fetch(`${config.API_BASE_URL}masters/cu_dst_master`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(payload),
      });

      const result = await response.json();

      if (result.success) {
        closeModal();
        getDsts();
      } else {
        setFormError(result.message || 'Could not save the DST.');
      }
    } catch (e) {
      setFormError('Something went wrong while saving.');
    } finally {
      setSaving(false);
    }
  };

  // ---- excel import handlers ----
  const openImportModal = () => {
    setExcelFile(null);
    setImportError('');
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setExcelFile(null);
    setImportError('');
  };

  const handleImportSubmit = async () => {
    if (!excelFile) {
      setImportError('Please select an excel file.');
      return;
    }

    try {
      setImporting(true);
      setImportError('');

      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('user_id', userId);

      // NOTE: file upload is sent as multipart/form-data (not encryptData),
      // same pattern used for upload_km_run_history.
      const response = await fetch(`${config.API_BASE_URL}masters/upload_dst_master`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        closeImportModal();
        getDsts();
      } else {
        setImportError(result.message || 'Could not import the excel file.');
      }
    } catch (e) {
      setImportError('Something went wrong while importing.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Container maxWidth="xl">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <Typography variant="h4">DST Master</Typography>
          <div className="d-flex gap-2">
            <Button variant="outlined" onClick={openImportModal}>
              <i className="bi bi-upload me-2" />
              Import Excel
            </Button>
            <Button variant="contained" onClick={openAddModal}>
              + Add DST
            </Button>
          </div>
        </div>

        {error && <div className="text-danger mb-3">{error}</div>}

        <div className="table-wrap">
          <table className="table table-hover table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: 70 }}>#</th>
                <th>ID</th>
                <th>DST Name</th>
                <th>Created By</th>
                <th>Created At</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 90 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : dsts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    No DST records found.
                  </td>
                </tr>
              ) : (
                dsts.map((row, index) => (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td>{row.formatetdID || row.id}</td>
                    <td>{row.dst_name || 'N/A'}</td>
                    <td>{row.createdByName || 'N/A'}</td>
                    <td>{row.created_date || 'N/A'}</td>
                    <td>
                      <span className={row.statusName === 'Active' ? 'badge bg-success' : 'badge bg-danger'}>
                        {row.statusName === 'Active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        title="Edit"
                        onClick={() => openEditModal(row)}
                      >
                        <i className="bi bi-pencil-square" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Add / Edit Modal */}
      <Modal open={showModal} onClose={closeModal}>
        <Box
          sx={{
            width: 450,
            maxWidth: '95vw',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">{form.id ? 'Edit DST' : 'Add DST'}</h4>
            <button type="button" className="btn-close" onClick={closeModal} />
          </div>

          {formError && <div className="text-danger mb-3">{formError}</div>}

          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-bold">DST Name</label>
              <input
                type="text"
                name="dst_name"
                className="form-control"
                value={form.dst_name}
                onChange={handleChange}
                placeholder="Enter DST Name"
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-bold">Status</label>
              <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </select>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outlined" onClick={closeModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Box>
      </Modal>

      {/* Excel Import Modal */}
      <Modal open={showImportModal} onClose={closeImportModal}>
        <Box
          sx={{
            width: 500,
            maxWidth: '95vw',
            bgcolor: '#fff',
            p: 3,
            borderRadius: 2,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Import DST Excel</h4>
            <button type="button" className="btn-close" onClick={closeImportModal} />
          </div>

          <p className="text-muted small mb-3">
            Excel should have a single column with header <strong>DST Name</strong> in row 1, and DST names listed
            below it.
          </p>

          {importError && <div className="text-danger mb-3">{importError}</div>}

          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            className="form-control mb-3"
            onChange={(e) => setExcelFile(e.target.files[0])}
          />

          <div className="d-flex justify-content-end gap-2">
            <Button variant="outlined" onClick={closeImportModal} disabled={importing}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleImportSubmit} disabled={importing}>
              {importing ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default DstMaster;